/**
 * The two player settings that must outlive a navigation: the volume pair and the playback speed.
 *
 * §7's volume row says they are persisted **separately** and that a muted-autoplay fallback must
 * never be written back into the stored level. Both halves of that are enforced structurally rather
 * than by remembering:
 *
 * - `volume` and `muted` are two fields, so unmuting restores the level the user last chose instead
 *   of whatever the element happened to hold while it was silent.
 * - Nothing here is ever called from the status poll. Every write below is on a *user action* path
 *   (`setVolume`, `toggleMute`, the speed menu), and the only code that mutes without being asked —
 *   `PlayerPort.play()`'s muted retry — has no route to this module at all. A store written from the
 *   poll would silently make one blocked autoplay the user's new preference, which is the exact
 *   failure §7 names.
 *
 * Track preferences (quality, audio language, subtitles) deliberately do **not** live here. They are
 * resolved against whatever tracks a given manifest turned out to have, so a persisted 1080p means
 * something different on the next video; Settings owns playback defaults as a real setting.
 */

const KEY = 'visious.player';

export interface PlayerPrefs {
  /** 0–1, independent of `muted`. */
  readonly volume: number;
  readonly muted: boolean;
  /** Snapped onto `PLAYBACK_RATES` by core before use, so a stale build's value cannot stick. */
  readonly playbackRate: number;
}

/** The pair the level controls move, which is the only part of the store they may touch. */
export type VolumePrefs = Pick<PlayerPrefs, 'volume' | 'muted'>;

export const DEFAULT_PLAYER_PREFS: PlayerPrefs = { volume: 1, muted: false, playbackRate: 1 };

function clamp01(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : null;
}

export function readPlayerPrefs(): PlayerPrefs {
  let raw: unknown;
  try {
    const text = globalThis.localStorage.getItem(KEY);
    if (text === null) return DEFAULT_PLAYER_PREFS;
    raw = JSON.parse(text) as unknown;
  } catch {
    // Storage can throw outright in a partitioned context, and a half-written value is possible on
    // any origin. Neither is a reason to start the player in an unknown state.
    return DEFAULT_PLAYER_PREFS;
  }

  if (typeof raw !== 'object' || raw === null) return DEFAULT_PLAYER_PREFS;
  const record = raw as Record<string, unknown>;
  const rate = record['playbackRate'];

  return {
    volume: clamp01(record['volume']) ?? DEFAULT_PLAYER_PREFS.volume,
    muted: record['muted'] === true,
    playbackRate:
      typeof rate === 'number' && Number.isFinite(rate) && rate > 0
        ? rate
        : DEFAULT_PLAYER_PREFS.playbackRate,
  };
}

export function writePlayerPrefs(prefs: PlayerPrefs): void {
  try {
    globalThis.localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // A preference that cannot be saved is not worth failing playback over.
  }
}

/**
 * Store the pair and leave the rate where it is — the one write a level control makes.
 *
 * Field by field rather than by spreading the caller's object: the two callers hold the pair as their
 * own state, and a spread would write whatever else that state happened to carry over the rate.
 */
export function writeVolumePrefs(pair: VolumePrefs): void {
  writePlayerPrefs({ ...readPlayerPrefs(), volume: pair.volume, muted: pair.muted });
}

/**
 * The pair after the reader sets a **level**, with §7's un-mute rule applied: raising the volume off
 * zero is how a reader un-mutes, and dragging it back to zero leaves the mute switch alone.
 *
 * The same two lines `PlayerPort.setVolume` runs on the element (`player/shakaPlayer.ts`), and they are
 * here as well for the one control that has no element to run them on — the corner box that says the
 * queue has ended (`playback/QueueEnded.tsx`) sets the level the next video will start at, so it has to
 * answer "is sound on" the way the player would.
 */
export function volumeAtLevel(pair: VolumePrefs, level: number): VolumePrefs {
  const volume = clamp01(level) ?? pair.volume;
  return { volume, muted: volume > 0 ? false : pair.muted };
}
